"use client";

import { cn } from "~/lib/utils";
import Image from "next/image";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Lead from "~/componentsDS/Typography/Lead";
import { InteractiveTimelineStep } from "./types";

export type SlideContentProps = {
    step: InteractiveTimelineStep;
    index: number;
    isActive: boolean;
    animKey: number;
    isCompact?: boolean;
};

const CARD_BG_BASE = "absolute inset-x-0 top-[30px] bottom-[10px] bg-pureWhite rounded-2xl shadow-[-65px_13px_19px_0_rgba(133,133,133,0),_-42px_8px_17px_0_rgba(133,133,133,0.01),_-23px_5px_14px_0_rgba(133,133,133,0.05),_-10px_2px_11px_0_rgba(133,133,133,0.09),_-3px_1px_6px_0_rgba(133,133,133,0.1)]";
const CARD_BG_DEFAULT_LG = "lg:rounded-3xl lg:inset-0 lg:rounded-none lg:shadow-none";
const CARD_BG_COMPACT_LG = "lg:rounded-3xl";

const SlideContent = ({ step, index, isActive, animKey, isCompact = false }: SlideContentProps) => {
    const { card } = step;
    const isFirst = index === 0;
    const lgCardOverride = isCompact ? CARD_BG_COMPACT_LG : CARD_BG_DEFAULT_LG;

    if (card.customContent) {
        if (!isActive) return <div className="h-full" />;
        return (
            <div className="relative h-full" key={animKey}>
                <div className={cn(CARD_BG_BASE, lgCardOverride)} />
                <div className={cn(
                    "absolute inset-x-0 top-[30px] bottom-[10px] overflow-hidden rounded-3xl",
                    !isCompact && "lg:inset-0 lg:rounded-none",
                )}>
                    {card.customContent}
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-full">
            <div className={cn(CARD_BG_BASE, lgCardOverride)} />

            {card.image && (
                <>
                    <div className={cn(
                        "hidden lg:block absolute overflow-hidden z-[1]",
                        isCompact
                            ? "inset-x-0 top-0 bottom-[10px] rounded-2xl"
                            : "inset-0",
                    )}>
                        <Image
                            src={card.image.desktop.url}
                            alt={card.image.desktop.alt || ""}
                            width={0}
                            height={0}
                            sizes={isCompact ? "100vw" : "50vw"}
                            priority={isFirst}
                            loading={isFirst ? "eager" : "lazy"}
                            className={cn("absolute right-0 top-0 h-full w-auto pointer-events-none", card.image.desktop.className)}
                        />
                    </div>
                    <div className="absolute inset-x-0 top-0 bottom-[10px] overflow-hidden rounded-2xl lg:hidden z-[1]">
                        <Image
                            src={card.image.mobile?.url || card.image.desktop.url}
                            alt={card.image.mobile?.alt || card.image.desktop.alt || ""}
                            width={0}
                            height={0}
                            sizes="100vw"
                            priority={isFirst}
                            loading={isFirst ? "eager" : "lazy"}
                            className={cn("absolute right-0 top-0 h-full w-auto pointer-events-none", card.image.mobile?.className)}
                        />
                    </div>
                </>
            )}

            {card.svgOverlay && isActive && (
                <div key={animKey} className={cn(
                    "absolute inset-x-0 top-[30px] bottom-[10px] overflow-hidden rounded-3xl pointer-events-none",
                    !isCompact && "lg:inset-0 lg:rounded-none",
                )}>
                    <div className="w-full h-full">
                        {card.svgOverlay}
                    </div>
                </div>
            )}

            <div className="hidden lg:flex absolute inset-0 flex-col z-[2] px-8 py-10">
                {card.stat && (
                    <Lead font="featureDisplay" size="xl" className="text-primary500 mb-2">{card.stat}</Lead>
                )}
                {card.description && (
                    <Paragraph size={isCompact ? "md" : "lg"} className="text-primary500 max-w-[220px]">
                        {card.description}
                    </Paragraph>
                )}
                {card.note && (
                    <Paragraph
                        size="xs"
                        className="text-gray400 mt-auto"
                    >
                        {card.note}
                    </Paragraph>
                )}
            </div>
        </div>
    );
};

export default SlideContent;
